import { useEffect, useState, useCallback } from 'react';
import {
  CalendarHeart,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  Bell,
  Pause,
  Play,
  XCircle,
  Loader2,
  Trash2,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useNavigate } from 'react-router-dom';

interface MonthlyPledge {
  id: number;
  pledgeNum: string;
  donorName: string;
  donorPhone: string;
  donorEmail?: string;
  monthlyAmount: number;
  paymentMethod: string;
  status: 'active' | 'paused' | 'cancelled' | 'pending';
  startDate: string;
  nextReminderDate?: string;
  totalMonths: number;
  totalDonated: number;
  createdAt: string;
}

interface PledgeListResponse {
  pledges: MonthlyPledge[];
  total: number;
  limit: number;
  offset: number;
}

const LIMIT = 20;

export default function AdminMonthlyPledges() {
  const navigate = useNavigate();
  const [pledges, setPledges] = useState<MonthlyPledge[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchPledges = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    query.set('limit', String(LIMIT));
    query.set('offset', String(offset));
    if (status) query.set('status', status);
    if (search) query.set('search', search);

    const res = await apiFetch<PledgeListResponse>(`/api/admin/monthly-pledges?${query.toString()}`);
    if (res.success && res.data) {
      setPledges(res.data.pledges);
      setTotal(res.data.total);
    }
    setLoading(false);
  }, [offset, status, search]);

  useEffect(() => {
    fetchPledges();
  }, [fetchPledges]);

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  const statusBadge = (s: MonthlyPledge['status']) => {
    const config: Record<string, { label: string; className: string }> = {
      active: { label: '進行中', className: 'bg-green-100 text-green-700' },
      paused: { label: '已暫停', className: 'bg-yellow-100 text-yellow-700' },
      cancelled: { label: '已取消', className: 'bg-gray-100 text-gray-700' },
      pending: { label: '待確認', className: 'bg-amber-100 text-amber-700' },
    };
    const c = config[s] || { label: s, className: 'bg-gray-100 text-gray-700' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.className}`}>
        {c.label}
      </span>
    );
  };

  const methodLabel = (m: string) => {
    const labels: Record<string, string> = {
      fps: 'FPS',
      'bank-transfer': '銀行轉賬',
    };
    return labels[m] || m;
  };

  const handleSendReminder = async (id: number) => {
    if (!confirm('確定要發送提醒給此月捐計劃的捐款人？')) return;
    setActionLoading(id);
    const res = await apiFetch<{ donationNum: string; sent: boolean }>(`/api/admin/monthly-pledges/${id}/remind`, {
      method: 'POST',
    });
    setActionLoading(null);
    if (res.success) {
      alert('提醒已發送！');
      fetchPledges();
    } else {
      alert(res.error?.message || '發送失敗');
    }
  };

  const handlePause = async (id: number) => {
    if (!confirm('確定要暫停此月捐計劃？')) return;
    setActionLoading(id);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'paused', paused_reason: '管理員手動暫停' }),
    });
    setActionLoading(null);
    if (res.success) {
      alert('計劃已暫停');
      fetchPledges();
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleResume = async (id: number) => {
    if (!confirm('確定要恢復此月捐計劃？')) return;
    setActionLoading(id);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'active' }),
    });
    setActionLoading(null);
    if (res.success) {
      alert('計劃已恢復');
      fetchPledges();
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleCancel = async (id: number) => {
    if (!confirm('確定要取消此月捐計劃？此操作無法撤銷。')) return;
    setActionLoading(id);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'cancelled', cancelled_reason: '管理員手動取消' }),
    });
    setActionLoading(null);
    if (res.success) {
      alert('計劃已取消');
      fetchPledges();
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleDelete = async (id: number, pledgeNum: string) => {
    if (!confirm(`確定要刪除月捐計劃 ${pledgeNum} 嗎？此操作無法撤銷。`)) return;
    setActionLoading(id);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, { method: 'DELETE' });
    setActionLoading(null);
    if (res.success) {
      alert('計劃已刪除');
      fetchPledges();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarHeart className="w-7 h-7 text-brand-600" />
          月捐計劃
        </h1>
      </div>

      {/* 篩選欄 */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索姓名 / 電話 / 編號"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">全部狀態</option>
            <option value="active">進行中</option>
            <option value="paused">已暫停</option>
            <option value="cancelled">已取消</option>
            <option value="pending">待確認</option>
          </select>
        </div>
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">編號</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">捐款人</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">WhatsApp</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">每月金額</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">付款方式</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">狀態</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">已捐月數</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">下次提醒</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pledges.length > 0 ? (
                pledges.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-gray-600 whitespace-nowrap">{p.pledgeNum}</td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                      <div className="font-medium">{p.donorName}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs font-mono">
                      {p.donorPhone || '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                      HK${(p.monthlyAmount / 100).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{methodLabel(p.paymentMethod)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{statusBadge(p.status)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.totalMonths}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {p.nextReminderDate
                        ? new Date(p.nextReminderDate).toLocaleDateString('zh-HK')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 flex-wrap">
                        {p.status === 'active' && (
                          <button
                            onClick={() => handleSendReminder(p.id)}
                            disabled={actionLoading === p.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-brand-50 text-brand-700 text-xs font-medium hover:bg-brand-100 transition-colors disabled:opacity-50"
                            title="發送提醒"
                          >
                            {actionLoading === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                            提醒
                          </button>
                        )}
                        {p.status === 'active' && (
                          <button
                            onClick={() => handlePause(p.id)}
                            disabled={actionLoading === p.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-yellow-50 text-yellow-700 text-xs font-medium hover:bg-yellow-100 transition-colors disabled:opacity-50"
                            title="暫停"
                          >
                            <Pause className="w-3 h-3" />
                            暫停
                          </button>
                        )}
                        {p.status === 'paused' && (
                          <button
                            onClick={() => handleResume(p.id)}
                            disabled={actionLoading === p.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                            title="恢復"
                          >
                            <Play className="w-3 h-3" />
                            恢復
                          </button>
                        )}
                        {(p.status === 'active' || p.status === 'paused' || p.status === 'pending') && (
                          <button
                            onClick={() => handleCancel(p.id)}
                            disabled={actionLoading === p.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-700 text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                            title="取消"
                          >
                            <XCircle className="w-3 h-3" />
                            取消
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/monthly-pledges/${p.id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
                          title="查看詳情"
                        >
                          <Eye className="w-3 h-3" />
                          查看
                        </button>
                        <button
                          onClick={() => handleDelete(p.id, p.pledgeNum)}
                          disabled={actionLoading === p.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                          title="刪除"
                        >
                          <Trash2 className="w-3 h-3" />
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    沒有符合條件的記錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={offset === 0}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 px-3">
            第 {currentPage} / {totalPages} 頁（共 {total} 筆）
          </span>
          <button
            onClick={() => setOffset((o) => Math.min((totalPages - 1) * LIMIT, o + LIMIT))}
            disabled={currentPage >= totalPages}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
